<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveOrg">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create organization') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 p-4 bg-yellow-600 shadow">
                    <p class="text-white font-medium">
                        {{ $t("All GdR Organizations must be operated by 'SixDesk SAT:GDR', some main input fields are disabled") }}.
                    </p>
                </div>
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Organization details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('This information will be displayed publicly') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="name"
                                            v-model="org.name"
                                            :placeholder="$t('Name')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Emails') }}</label>
                                    <input-email
                                        id="emails"
                                        ref="emails"
                                        :emailList="org.emails"
                                    ></input-email>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="phone">{{ $t('Phone') }}</label>
                                    <input-phone
                                        id="phones"
                                        ref="phones"
                                        :phoneList="org.phones"
                                    ></input-phone>
                                </div>
                            </div>
                        </div>
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Organization settings') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Organization settings') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="id_ext">{{ $t('External ID') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="id_ext"
                                            v-model="org.id_ext"
                                            :placeholder="$t('External ID')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            type="text"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="lang_id">{{ $t('Languages') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <select
                                            id="lang_id"
                                            v-model="org.lang_id"
                                            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                            <option :value="null" disabled>{{ $t('Select an option') }}</option>
                                            <option v-for="language in languages" :value="language.id">{{ language.name }}</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="server">{{ $t('Server') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="server"
                                            v-model.lazy="org.server_ids"
                                            :options="servers"
                                            option-label="name"
                                            required
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <div class="flex-shrink-0 inline-block">
                                                        <svg-vue class="w-5 h-auto object-scale-down text-gray-500" icon="font-awesome.server-solid"></svg-vue>
                                                    </div>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                    <div class="hidden xl:flex xl:items-center xl:gap-1 truncate font-normal text-gray-500">
                                                        <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.link-solid"></svg-vue>
                                                        <span>{{ props.option.url ? props.option.url : "-" }}</span>
                                                    </div>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="flex-shrink-0 inline-block">
                                                        <svg-vue class="w-5 h-auto object-scale-down text-gray-500" icon="font-awesome.server-solid"></svg-vue>
                                                    </div>
                                                    <div class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                    <div class="hidden xl:flex xl:items-center xl:gap-2 truncate font-normal text-gray-500">
                                                        <svg-vue class="h-3 w-3 text-gray-500" icon="font-awesome.link-solid"></svg-vue>
                                                        <span>{{ props.option.url ? props.option.url : "-" }}</span>
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <input-switch
                                        id="status"
                                        v-model="org.status"
                                        :disabled-label="$t('The organization is disabled')"
                                        :enabled-label="$t('The organization is activated')"
                                    ></input-switch>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('When the organization is deactivated, the users in a center from this organization will not be able to create tickets') }}.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/orgs"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Create organization') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: "new-org",
    metaInfo() {
        return {
            title: this.$i18n.t('Create organization')
        }
    },
    data() {
        return {
            languages: [],
            servers: [],
            org: {
                id_ext: null,
                name: null,
                emails: [],
                phones: [],
                avatar: null,
                avatar_preview: null,
                lang_id: null,
                server_ids: null,
                status: true,
            },
        }
    },
    computed: {
        loading() {
            return this.$store.state.loading.states.main;
        },
    },
    beforeMount() {
        this.$store.commit('resetLoading');
    },
    mounted() {
        this.getLanguages();
        this.getServers();
    },
    methods: {
        async saveOrg() {
            const self = this;
            self.$store.commit('loadSections');
            self.org.phones = [...await self.$refs.phones.savePhones()];
            self.org.emails = [...await self.$refs.emails.saveEmails()];
            axios.post('api/dashboard/admin/orgs', self.org).then(function (response) {
                self.$store.commit('unloadSections');
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/orgs/' + response.data.org.id + '/edit');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getLanguages() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/languages').then(function (response) {
                self.languages = response.data;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getServers() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/servers/list').then(function (response) {
                self.servers = response.data.servers;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        }
    }
}
</script>
